import { apiClient } from "../../config/api.config";

class NotificationService {
  /**
   * Get user notifications with optional filters
   * @param {String} queryString - URL query parameters
   * @returns {Promise} - Promise with notifications data and pagination
   */
  async getUserNotifications(queryString = "") {
    try {
      const response = await apiClient.get(`/notifications?${queryString}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get admin notifications
   * @param {String} queryString - URL query parameters
   * @returns {Promise} - Promise with admin notifications
   */
  async getAdminNotifications(queryString = "") {
    try {
      const response = await apiClient.get(
        `/notifications/admin?${queryString}`
      );

      // Ensure response has data and proper structure
      if (!response.data) {
        return {
          data: [],
          pagination: {
            page: 1,
            limit: 10,
            total: 0,
            pages: 1,
          },
        };
      }

      return {
        data: Array.isArray(response.data.data) ? response.data.data : [],
        pagination: response.data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 1,
        },
      };
    } catch (error) {
      return {
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          pages: 1,
        },
      };
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise} - Promise with unread count
   */
  async getUnreadCount() {
    try {
      const response = await apiClient.get("/notifications/unread-count");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * NEW: Get categorized notification counts
   * @returns {Promise} - Promise with categorized counts
   */
  async getCategorizedCounts() {
    try {
      const response = await apiClient.get("/notifications/categorized-counts");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * NEW: Get detailed unread count with category breakdown
   * @returns {Promise} - Promise with detailed unread counts
   */
  async getDetailedUnreadCount() {
    try {
      const response = await apiClient.get(
        "/notifications/detailed-unread-count"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * NEW: Get recent notifications grouped by category
   * @param {Number} limit - Number of notifications to fetch
   * @returns {Promise} - Promise with grouped notifications
   */
  async getRecentNotificationsByCategory(limit = 50) {
    try {
      const response = await apiClient.get(
        `/notifications/recent-by-category?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * NEW: Get notifications by specific category/sourceModel
   * @param {String} sourceModel - The category/sourceModel to filter by
   * @param {String} queryString - Additional query parameters
   * @returns {Promise} - Promise with categorized notifications
   */
  async getNotificationsByCategory(sourceModel, queryString = "") {
    try {
      const response = await apiClient.get(
        `/notifications/category/${sourceModel}?${queryString}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mark notifications as read
   * @param {Object} data - Object containing notification IDs to mark as read
   * @returns {Promise} - Promise with result
   */
  async markAsRead(data) {
    try {
      const response = await apiClient.post("/notifications/mark-read", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * NEW: Mark all notifications of a specific category as read
   * @param {String} sourceModel - The category/sourceModel to mark as read
   * @returns {Promise} - Promise with result
   */
  async markCategoryAsRead(sourceModel) {
    try {
      const response = await apiClient.post(
        `/notifications/category/${sourceModel}/mark-read`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a notification
   * @param {String} id - Notification ID
   * @returns {Promise} - Promise with result
   */
  async deleteNotification(id) {
    try {
      const response = await apiClient.delete(`/notifications/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get notifications by type
   * @param {String} type - Notification type (e.g., "kyc", "wallet", "portfolio")
   * @param {Boolean} isRead - Filter by read status
   * @returns {Promise} - Promise with filtered notifications
   */
  async getNotificationsByType(type, isRead = null) {
    let queryString = `type=${type}`;
    if (isRead !== null) {
      queryString += `&isRead=${isRead}`;
    }
    return this.getUserNotifications(queryString);
  }

  /**
   * Get admin notification statistics
   * @returns {Promise} - Promise with admin stats
   */
  async getAdminNotificationStats() {
    try {
      const response = await apiClient.get("/notifications/admin/stats");
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const notificationService = new NotificationService();
